"use client"

import type React from "react"
import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { AlertCircle, Mail, Phone, MapPin } from "lucide-react"
import Image from "next/image"

import { PhoneInput, defaultCountries, parseCountry } from "react-international-phone"
import "react-international-phone/style.css"

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    whatsapp: "",
    message: "",
  })

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    whatsapp: "",
    contact: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const priorityIso2 = ["us", "gb", "au", "it"] // ← меняй порядок здесь, если нужно

const allowedCountries = defaultCountries.sort((a, b) => {
  const isoA = parseCountry(a).iso2
  const isoB = parseCountry(b).iso2

  const indexA = priorityIso2.indexOf(isoA as any)
  const indexB = priorityIso2.indexOf(isoB as any)

  if (indexA !== -1 && indexB !== -1) return indexA - indexB
  if (indexA !== -1) return -1
  if (indexB !== -1) return 1
  return 0
})

  const validateForm = () => {
    const newErrors = { name: "", email: "", whatsapp: "", contact: "" }

    // Имя обязательно
    if (!formData.name.trim()) {
      newErrors.name = "Please enter your name"
    }

    // Email — проверяем только если введён
    if (formData.email.trim()) {
      if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
        newErrors.email = "Please enter a valid email address (e.g. name@example.com)"
      }
    }

    // WhatsApp — проверяем только если введены цифры (пользователь начал вводить номер)
    const whatsappDigits = formData.whatsapp.replace(/[^\d]/g, "")
    // if (whatsappDigits.length > 0 && whatsappDigits.length < 7 && ) {
    //   newErrors.whatsapp = "Please enter a valid phone number"
    // }

    // Валидные контакты
    const hasValidEmail = formData.email.trim() && /^\S+@\S+\.\S+$/.test(formData.email.trim())
    const hasValidWhatsapp = whatsappDigits.length >= 7

    // Хотя бы один контакт обязателен
    if (!hasValidEmail && !hasValidWhatsapp) {
      newErrors.contact = "Please provide at least one contact: Email or WhatsApp"
    }

    setErrors(newErrors)
    return !Object.values(newErrors).some((err) => err)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim() || undefined,
          whatsapp: formData.whatsapp || undefined,
          message: formData.message.trim() || undefined,
        }),
      })

      if (res.ok) {
        setIsSubmitted(true)
        setFormData({ name: "", email: "", whatsapp: "", message: "" })
        setErrors({ name: "", email: "", whatsapp: "", contact: "" })
      } else {
        const data = await res.json()
        alert("Ошибка: " + (data.error || "Не удалось отправить сообщение"))
      }
    } catch (error) {
      console.error("Ошибка отправки:", error)
      alert("Ошибка соединения. Попробуйте позже.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setErrors({ name: "", email: "", whatsapp: "", contact: "" })
  }

  return (
    <section id="contact" className="py-12 sm:py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-7xl mx-auto lg:h-[640px] mb-16 lg:mb-32">

          {/* Левая часть — фото */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-[380px] sm:h-[480px] lg:h-full rounded-2xl lg:rounded-3xl overflow-hidden shadow-xl order-2 lg:order-1"
          >
            <Image
              src="/images/heros/3.webp"
              alt="Greek Theater Taormina"
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 600px"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
            <div className="relative h-full flex flex-col justify-between p-6 sm:p-8 lg:p-10">
              <div>
                <span className="inline-block bg-primary text-white px-4 py-2 rounded-full text-sm font-medium">
                  Make it Happen
                </span>
              </div>
              <div className="space-y-4 lg:space-y-6">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-semibold text-white leading-tight">
                  Whether you're planning a romantic getaway, a family adventure, or a destination wedding, I'm here to
                  help make your Taormina dreams a reality.
                </h3>
                <div className="space-y-3 text-white/90">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 flex-shrink-0" />
                    <span className="font-sans text-sm lg:text-base break-all">
                      info@yourtaorminaconcierge.com
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 flex-shrink-0" />
                    <span className="font-sans text-sm lg:text-base">
                      +39 371 442 5396
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 flex-shrink-0" />
                    <span className="font-sans text-sm lg:text-base">
                      Taormina, Sicily, Italy
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Правая часть — форма */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-stretch order-1 lg:order-2"
          >
            {isSubmitted ? (
              <div className="bg-muted/50 rounded-2xl lg:rounded-3xl p-8 lg:p-12 shadow-xl flex flex-col items-center justify-center text-center w-full h-full">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl lg:text-3xl font-serif font-semibold mb-3">
                  Thank You!
                </h3>
                <p className="text-base lg:text-lg text-muted-foreground mb-8 max-w-md">
                  I've received your message and will get back to you shortly.
                </p>
                <Button onClick={() => setIsSubmitted(false)} className="px-8 py-6 text-base">
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form
                noValidate
                onSubmit={handleSubmit}
                className="bg-muted/50 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-xl w-full h-full flex flex-col"
              >
                <div className="space-y-2">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold">
                    Get in Touch
                  </h2>
                  <p className="text-sm lg:text-base text-muted-foreground">
                    Let's create your perfect journey together.
                  </p>
                </div>

                <div className="flex-1 flex flex-col justify-between space-y-6 mt-6">
                  <div className="space-y-5">

                    {/* Name */}
                    <div className="space-y-1.5">
                      <Label className="uppercase text-xs tracking-wider text-muted-foreground">
                        Name *
                      </Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        placeholder="John Doe"
                        className="h-12 rounded-lg border-none bg-background/70 focus-visible:ring-primary"
                      />
                      {errors.name && (
                        <p className="flex items-center gap-1.5 text-red-500 text-xs mt-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <Label className="uppercase text-xs tracking-wider text-muted-foreground">
                        Email
                      </Label>
                      <Input
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        placeholder="john@example.com"
                        className="h-12 rounded-lg border-none bg-background/70 focus-visible:ring-primary"
                      />
                      {errors.email && (
                        <p className="flex items-center gap-1.5 text-red-500 text-xs mt-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    {/* WhatsApp — полностью пустое, код появляется только после выбора страны */}
                    <div className="space-y-1.5 relative">
                      <Label className="uppercase text-xs tracking-wider text-muted-foreground">
                        WhatsApp
                      </Label>
                      <div className="h-12 rounded-lg bg-background/70 flex items-center ring-offset-background focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 transition-all">
                        <PhoneInput
                          value={formData.whatsapp}
                          onChange={(phone) => handleInputChange("whatsapp", phone)}
                          countries={allowedCountries}
                          placeholder="+1 212 555 1234"
                          defaultCountry={undefined}
                           // ← Поле пустое при загрузке
                          // Нет initialCountry — пользователь сам выбирает страну и код
                          inputStyle={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            background: "transparent",
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            paddingTop: "3px",
                            fontSize: "1rem",
                            color: "hsl(var(--foreground))",
                            outline: "none",
                          }}
                          countrySelectorStyleProps={{
                            buttonStyle: {
                              height: "100%",
                              border: "none",
                              background: "transparent",
                              borderRadius: "0.5rem 0 0 0.5rem",
                              padding: "0 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                            },
                          }}
                        />
                      </div>
                      {errors.whatsapp && (
                        <p className="flex items-center gap-1.5 text-red-500 text-xs mt-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.whatsapp}
                        </p>
                      )}
                    </div>

                    {errors.contact && (
                      <p className="flex items-center gap-1.5 text-red-500 text-sm">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        {errors.contact}
                      </p>
                    )}

                    <p className="text-xs text-muted-foreground">
                      * Please provide at least one: Email or WhatsApp
                    </p>

                    {/* Message */}
                    <div className="space-y-1.5">
                      <Label className="uppercase text-xs tracking-wider text-muted-foreground">
                        Message (optional)
                      </Label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        rows={5}
                        placeholder="Tell me about your plans..."
                        className="min-h-[140px] resize-none rounded-lg border-none bg-background/70 focus-visible:ring-primary"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 text-base font-medium rounded-lg bg-primary hover:bg-primary/90 mt-6"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  )
}